/* eslint-disable react/no-unknown-property */

import { ComponentResource } from '@/api/serverApi'
import { ActiveDesignSlice, setSelectedComponent } from '@/stores/active-design'
import { useTexture } from '@react-three/drei'
import concreteTexture from '@/assets/textures/concrete.jpg'
import { useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { PropertyTabs, setCurrentPropertiesTab } from '@/stores/selected-properties-tab'
import { RootState } from '@/stores'
import { ThreeColors } from '@/helpers/threeColors'
import { Geometry, Base, Subtraction, CSGGeometryRef } from '@react-three/csg'
import useComponentProduct from '@/hooks/useComponentProduct'
import DistanceArrow3D from './DistanceArrow3D'
import { BoxGeometry, RepeatWrapping, Vector3 } from 'three'
import EdgeDistanceValues from '@/consts/EdgeDistanceValues'

const distanceArrowHorizontalOffsetFromSlab = 30
const referenceOffsetFromArrow = 15
const textureOpacity = 0.66

type ConcreteSlab3DProps = {
  component: ComponentResource
  design: ActiveDesignSlice['design']
}

type DistancePointType = {
  label: string,
  distanceArrowStartPoint: [number, number, number],
  distanceArrowEndPoint: [number, number, number],
  startReferenceLinePoints: [[number, number, number], [number, number, number]],
  endReferenceLinePoints: [[number, number, number], [number, number, number]],
}

const ConcreteSlab3D = ({ component, design }: ConcreteSlab3DProps) => {
  const dispatch = useDispatch()

  const selectedCameraControlType = useSelector((state: RootState) => state.selectedCameraControlType.option)

  const csg = useRef<CSGGeometryRef | null>(null)

  const details = component.concrete_slab!

  const channel = design?.components.find(c => c.parent_id === component.id)
  const channelLength = channel?.cast_in_anchor_channel?.custom_length ?? channel?.cast_in_anchor_channel?.length ?? 0

  const { productVariant } = useComponentProduct({ component: channel })

  const isSelected = useSelector((state: RootState) => state.activeDesign.selectedComponentId  === component.id)

  const colorMap = useTexture(concreteTexture)

  const [isHovered, setIsHovered] = useState(false)

  // ****************************************************************************************************
  // Memoized slab values
  // ****************************************************************************************************

  const slab = useMemo(() => {
    // Subdividing the geometry by the exact length produces excessive vertices and causes
    // performance problems, so this should reduce the amount of vertices generated. This
    // affects the frequency of the wave on the edge of the slab. Higher is more frequent
    const subdivisionMultiplier = 0.075

    // The wave multiplier affects the amplitude of the wave on the edge of the slab. Higher
    // is more pronounced
    const waveMultiplier = 5

    const isEdgeDistanceXPosInfinite = !!(details.is_edge_distance_x_pos_infinite ?? false)
    const isEdgeDistanceXNegInfinite = !!(details.is_edge_distance_x_neg_infinite ?? false)
    const isEdgeDistanceYPosInfinite = !!(details.is_edge_distance_y_pos_infinite ?? false)
    const isEdgeDistanceYNegInfinite = !!(details.is_edge_distance_y_neg_infinite ?? false)

    const xPosEdgeDistance = isEdgeDistanceXPosInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_x_pos
    const xNegEdgeDistance = isEdgeDistanceXNegInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_x_neg
    const yPosEdgeDistance = isEdgeDistanceYPosInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_y_pos
    const yNegEdgeDistance = isEdgeDistanceYNegInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_y_neg

    const xEdgeLength = xNegEdgeDistance + xPosEdgeDistance
    // Add the width of the channel to the Y dimension so it's considered in the overall size of the slab
    const yEdgeLength = yNegEdgeDistance + yPosEdgeDistance + (productVariant?.cast_in_anchor_channel?.width ?? 0)

    const slab = new BoxGeometry(
      channelLength + xEdgeLength,
      details.thickness,
      yEdgeLength,
      // Subdivide the slab across the edges to allow for vertex transformation
      (channelLength + xEdgeLength) * subdivisionMultiplier,
      1,
      yEdgeLength * subdivisionMultiplier,
    )

    const slabPosition = slab.getAttribute('position')
    const v3 = new Vector3()

    for (let i = 0; i < slabPosition.count; i++) {
      v3.fromBufferAttribute(slabPosition, i)

      // If the X position is equal to +/- half the calculation of the total X dimension of the slab
      // then the vertex is on the edge of the slab
      if (v3.x === (channelLength + xEdgeLength) / 2 && isEdgeDistanceXPosInfinite) {
        // If on the positive edge, add a sine wave offset to create a wavy edge. This signifies an infinite
        // boundary
        slabPosition.setX(i, v3.x + Math.sin(v3.z) * waveMultiplier)
        // If on the negative edge, subtract the sine wave to ensure the edge is always extruded, not inset
      } else if (v3.x === -(channelLength + xEdgeLength) / 2 && isEdgeDistanceXNegInfinite) {
        slabPosition.setX(i, v3.x - Math.sin(v3.z) * waveMultiplier)
      }

      // Slight rounding issues with decimal channel depth and width values can cause difficulty with accurately
      // determining the edge of the slab
      const truncatedZValue = parseFloat(v3.z.toFixed(3))
      const truncatedEdgeLengthValue = parseFloat(((yEdgeLength) / 2).toFixed(3))

      // If the Y position is equal to +/- half the calculation of the total Y dimension of the slab
      // then the vertex is on the edge of the slab
      if (truncatedZValue === truncatedEdgeLengthValue && isEdgeDistanceYNegInfinite) {
        // If on the positive edge, add a sine wave offset to create a wavy edge. This signifies an infinite
        // boundary
        slabPosition.setZ(i, v3.z + Math.sin(v3.x) * waveMultiplier)
      } else if (truncatedZValue === -truncatedEdgeLengthValue && isEdgeDistanceYPosInfinite) {
        // If on the negative edge, subtract the sine wave to ensure the edge is always extruded, not inset
        slabPosition.setZ(i, v3.z - Math.sin(v3.x) * waveMultiplier)
      }

    }

    slab.computeVertexNormals()

    return slab
  }, [channelLength, details.edge_distance_x_neg, details.edge_distance_x_pos, details.edge_distance_y_neg, details.edge_distance_y_pos, details.is_edge_distance_x_neg_infinite, details.is_edge_distance_x_pos_infinite, details.is_edge_distance_y_neg_infinite, details.is_edge_distance_y_pos_infinite, details.thickness, productVariant?.cast_in_anchor_channel?.width])

  useMemo(() => {
    const tileMultiplier = 500

    colorMap.wrapS = RepeatWrapping
    colorMap.wrapT = RepeatWrapping

    // We want the tiling to repeat based on the total dimensions of the slab, with a multiplier
    // to stop the texture from applying on every subdivided face
    const xTileRatio = (details.edge_distance_x_neg + channelLength + details.edge_distance_x_pos) / tileMultiplier
    const yTileRatio = (details.edge_distance_y_neg + details.edge_distance_y_pos) / tileMultiplier

    colorMap.repeat.set(xTileRatio, yTileRatio)
  }, [channelLength, colorMap, details.edge_distance_x_neg, details.edge_distance_x_pos, details.edge_distance_y_neg, details.edge_distance_y_pos])


  // ****************************************************************************************************
  // Memoized subtraction values for the inset for the cast-in channel
  // ****************************************************************************************************

  const subtractionGeometry = useMemo(() => {
    return (
      <boxGeometry
        args={[
          channelLength ?? 0,
          // The additional 0.1 is needed to reduce overlapping geometry. This appears to cause
          // some issues with the CSG operation that caused crashes
          (productVariant?.cast_in_anchor_channel?.depth ?? 0) + 0.1,
          (productVariant?.cast_in_anchor_channel?.width ?? 0),
        ]}
      />
    )
  }, [channelLength, productVariant])

  const channelOffset = useMemo(() => {
    const isEdgeDistanceXPosInfinite = !!(details.is_edge_distance_x_pos_infinite ?? false)
    const isEdgeDistanceXNegInfinite = !!(details.is_edge_distance_x_neg_infinite ?? false)
    const isEdgeDistanceYPosInfinite = !!(details.is_edge_distance_y_pos_infinite ?? false)
    const isEdgeDistanceYNegInfinite = !!(details.is_edge_distance_y_neg_infinite ?? false)

    const xPosEdgeDistance = isEdgeDistanceXPosInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_x_pos
    const xNegEdgeDistance = isEdgeDistanceXNegInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_x_neg
    const yPosEdgeDistance = isEdgeDistanceYPosInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_y_pos
    const yNegEdgeDistance = isEdgeDistanceYNegInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_y_neg

    const halfThickness = details.thickness / 2
    const halfChannelDepth = (productVariant?.cast_in_anchor_channel?.depth ?? 0) / 2
    const halfChannelWidth = (productVariant?.cast_in_anchor_channel?.width ?? 0) / 2
    const halfSlabHeight = (yPosEdgeDistance + yNegEdgeDistance + (productVariant?.cast_in_anchor_channel?.width ?? 0)) / 2
    const halfSlabWidth = (xPosEdgeDistance + xNegEdgeDistance + channelLength) / 2
    const halfChannelLength = channelLength / 2

    // The additional 0.1 is needed to reduce overlapping geometry. This appears to cause
    // some issues with the CSG operation that caused crashes
    const yOffset = (halfThickness - halfChannelDepth) + 0.1
    const xOffset = (halfSlabWidth - halfChannelLength - xPosEdgeDistance)
    const zOffset = (halfSlabHeight - halfChannelWidth - yNegEdgeDistance)

    return new Vector3(xOffset, yOffset, zOffset)
  }, [details, productVariant, channelLength])

  const thicknessArrow : DistancePointType = useMemo(() => {
    const isEdgeDistanceXPosInfinite = !!(details.is_edge_distance_x_pos_infinite ?? false)
    const isEdgeDistanceXNegInfinite = !!(details.is_edge_distance_x_neg_infinite ?? false)
    const isEdgeDistanceYPosInfinite = !!(details.is_edge_distance_y_pos_infinite ?? false)
    const isEdgeDistanceYNegInfinite = !!(details.is_edge_distance_y_neg_infinite ?? false)
    const isThicknessInfinite = !!(details.is_thickness_infinite ?? false)

    const xPosEdgeDistance = isEdgeDistanceXPosInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_x_pos
    const xNegEdgeDistance = isEdgeDistanceXNegInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_x_neg
    const yPosEdgeDistance = isEdgeDistanceYPosInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_y_pos
    const yNegEdgeDistance = isEdgeDistanceYNegInfinite ? EdgeDistanceValues.defaultEdgeDistance : details.edge_distance_y_neg

    const halfThickness = details.thickness / 2
    const halfSlabHeight = (yPosEdgeDistance + yNegEdgeDistance + (productVariant?.cast_in_anchor_channel?.width ?? 0)) / 2
    const halfSlabWidth = (xPosEdgeDistance + xNegEdgeDistance + channelLength) / 2

    const arrowStartPoint = [
      -(halfSlabWidth + distanceArrowHorizontalOffsetFromSlab),
      halfThickness,
      halfSlabHeight + distanceArrowHorizontalOffsetFromSlab,
    ]

    const arrowEndPoint = [
      -(halfSlabWidth + distanceArrowHorizontalOffsetFromSlab),
      -halfThickness,
      halfSlabHeight + distanceArrowHorizontalOffsetFromSlab,
    ]

    const startReferenceLinePoints = [
      [
        -halfSlabWidth,
        halfThickness,
        halfSlabHeight,
      ],
      [
        -(halfSlabWidth + distanceArrowHorizontalOffsetFromSlab + referenceOffsetFromArrow),
        halfThickness,
        halfSlabHeight + distanceArrowHorizontalOffsetFromSlab + referenceOffsetFromArrow,
      ],
    ]

    const endReferenceLinePoints = [
      [
        -halfSlabWidth,
        -halfThickness,
        halfSlabHeight,
      ],
      [
        -(halfSlabWidth + distanceArrowHorizontalOffsetFromSlab + referenceOffsetFromArrow),
        -halfThickness,
        halfSlabHeight + distanceArrowHorizontalOffsetFromSlab + referenceOffsetFromArrow,
      ],
    ]

    return {
      label: `${isThicknessInfinite ? '∞' : Math.abs(details.thickness)} mm`,
      distanceArrowStartPoint: arrowStartPoint as [number, number, number],
      distanceArrowEndPoint: arrowEndPoint as [number, number, number],
      startReferenceLinePoints: startReferenceLinePoints as [[number, number, number], [number, number, number]],
      endReferenceLinePoints: endReferenceLinePoints as [[number, number, number], [number, number, number]],
    }

  }, [details, productVariant, channelLength])

  const selectedMaterial = (
    <meshStandardMaterial
      map={colorMap}
      wireframe={false}
      color={ThreeColors.selectedColor}
      opacity={textureOpacity}
      transparent
    />
  )

  const hoveredMaterial = (
    <meshStandardMaterial
      map={colorMap}
      wireframe={false}
      color={ThreeColors.hoverColor}
      opacity={textureOpacity}
      transparent
    />
  )

  const defaultMaterial = (
    <meshStandardMaterial
      map={colorMap}
      wireframe={false}
      color={ThreeColors.defaultColor}
      opacity={textureOpacity}
      transparent
    />
  )

  return (
    <mesh
      onClick={(e) => {
        e.stopPropagation()

        if (selectedCameraControlType !== 'cursor') {
          return
        }

        dispatch(setSelectedComponent(component.id))
        dispatch(setCurrentPropertiesTab(PropertyTabs.Selected))
      }}
      onPointerEnter={(e) => {
        e.stopPropagation()

        if (selectedCameraControlType !== 'cursor') {
          return
        }

        setIsHovered(true)
      }}
      onPointerLeave={(e) => {
        e.stopPropagation()

        if (selectedCameraControlType !== 'cursor') {
          return
        }

        setIsHovered(false)
      }}
    >
      {
        thicknessArrow && (
          <DistanceArrow3D
            points={[thicknessArrow.distanceArrowStartPoint, thicknessArrow.distanceArrowEndPoint]}
            referenceLinePoints={[thicknessArrow.startReferenceLinePoints, thicknessArrow.endReferenceLinePoints]}
            label={thicknessArrow.label}
          />
        )
      }
      <Geometry ref={csg} useGroups>
        <Base>
          <primitive object={slab} />
          {
            isSelected
              ? selectedMaterial
              : (
                isHovered
                  ? hoveredMaterial
                  : defaultMaterial
              )
          }
        </Base>
        <Subtraction position={channelOffset}>
          {subtractionGeometry}
          {
            isSelected
              ? selectedMaterial
              : (
                isHovered
                  ? hoveredMaterial
                  : defaultMaterial
              )
          }
        </Subtraction>
      </Geometry>
    </mesh>
  )
}

export default ConcreteSlab3D
